import Dexie, { type EntityTable, type UpdateSpec } from 'dexie'
import {
  INTERNAL_MEETING_MAX_DURATION_MINUTES,
  MINUTES_PER_DAY,
  appointmentFields,
  internalMeetingDays,
  shiftDays,
} from '@ghp/domain'
import type {
  FreeInspectionInput,
  AssignedInquiry,
  DepositConflictCode,
  InspectionResult,
  InspectionSubmission,
  PassageNoticeReason,
  PlannedInternalMeeting,
  RecordInspectionStep,
  SubmittedAnomaly,
  SurveyV2,
  TourInternalMeetings,
} from '@ghp/domain'

/**
 * Le **troisième modèle de données** du projet (`apps/terrain/CLAUDE.md`) : ni le domaine, ni
 * les tables serveur. Il dérive de `@ghp/domain` **plus** ce que seule la tablette connaît —
 * état d'envoi, clé d'idempotence, horodatage local.
 *
 * ⚠️ Ce schéma est **versionné** (`db.version(…)` plus bas fait foi) : toute évolution passe par
 * une **migration Dexie**, jamais par un effacement. Une mise à jour de l'app ne purge pas les
 * données locales — la fiche que l'agent n'a pas encore transmise n'existe nulle part ailleurs.
 *
 * ⚠️ **Une base par équipe** (D22 pt.4) : ce fichier décrit ce qu'une **tournée** contient, pour
 * une équipe et une seule. Le référentiel d'anomalies et le journal de diagnostic ne sont la
 * saisie de personne et vivent à côté, dans `offline/shared-db.ts`.
 */

/**
 * Une demande de la tournée telle que la **tablette** la garde : la vue du domaine, plus deux
 * champs que seule elle connaît. Le serveur n'en sert aucun des deux.
 */
export type CachedInquiry = AssignedInquiry & {
  /**
   * Quand la descente a **reçu cette demande du serveur** pour la dernière fois — horodatage
   * local, posé par {@link cacheInquiries} sur le seul chemin qui écrit cette table.
   *
   * C'est ce qui distingue « transmis » de « renvoyé au terrain », et rien d'autre ne le peut :
   * `pullInquiries` ne sert que `assigned` et `in_progress`, donc le **statut** en cache est le
   * même avant un dépôt et après un renvoi du bureau. Ce qui change est la date : le serveur
   * cesse de servir la demande le temps qu'elle est `submitted`, et la ressert le jour où le
   * bureau la rouvre. Comparé au `sentAt` du dernier envoi, cet horodatage dit lequel des deux
   * faits est le plus récent (`features/inspections/lib/list-filters.ts`).
   *
   * ⚠️ **Pas indexé, donc pas de migration** : Dexie n'exige une version neuve que pour un
   * `.stores()` neuf. Les lignes descendues avant ce champ n'en portent pas, et `undefined` s'y
   * lit comme « jamais reservie depuis » — le classement d'avant, à l'identique.
   */
  servedAt?: string
  /**
   * Le **jour** du rendez-vous à l'heure de Paris (`2026-08-04`), dérivé de `scheduledAt` à
   * l'écriture — le seul champ indexé que le serveur ne sert pas.
   *
   * C'est la clé par laquelle l'agenda lit une semaine sans balayer le cache : celui-ci garde
   * **tout contrôle saisi** sur la tablette, transmis compris (`offline/pull.ts` ne retire que ce
   * que personne n'a touché), il grandit donc sur toute sa vie, et une semaine relue en mémoire
   * coûterait ce balayage à chaque flèche « semaine suivante ».
   *
   * ⚠️ **Un jour, pas l'instant ISO**, et ce n'est pas une commodité d'affichage. `scheduledAt`
   * accepte un décalage (`z.iso.datetime({ offset: true })`) : deux instants équivalents
   * peuvent s'écrire `…T08:30:00Z` et `…T10:30:00+02:00`, qui ne se **comparent pas** comme des
   * chaînes. Un index de plage posé dessus rendrait des semaines fausses le jour où une réponse
   * cesserait d'être normalisée en UTC — sans erreur. Un jour de calendrier, lui, s'ordonne
   * lexicographiquement par construction.
   *
   * ⚠️ **Absent ou `null` quand aucun rendez-vous n'est pris**, et IndexedDB n'indexe pas une
   * ligne dont la clé indexée manque : ces demandes sont donc **hors de l'agenda**, ce qui est
   * exactement leur place. Elles restent dans la tournée, lue par `toArray()`.
   */
  scheduledDay?: string | null
  /**
   * Ce que l'agent a écrit en ouvrant un **contrôle libre** (#419, M41) — une ligne que la tablette
   * a fabriquée, que le serveur n'a jamais servie. Absent sur toute ligne descendue.
   *
   * C'est ce qui la fait transmettre avec sa saisie, lire « En attente de rattachement » une fois
   * partie, et ne jamais marquer « sortie de la tournée ». **Local, non indexé** : aucune migration.
   */
  freeInspection?: FreeInspectionInput & { createdAt: string }
}

/**
 * Un **point interne** de l'équipe (M28) tel que la tablette le garde : le créneau servi, plus
 * le jour qui l'indexe — le même que {@link CachedInquiry.scheduledDay}, et pour la même raison.
 *
 * ⚠️ **Sa propre table, et non une ligne de `inquiries`** : un point interne n'a ni demande, ni
 * usager, ni relevé. Le ranger avec la tournée le ferait apparaître dans « Mes contrôles », et
 * chaque lecture de la tournée devrait l'en exclure — c'est l'argument qui le tient hors
 * d'`Appointment` au serveur (`internalMeetingSchema`), il vaut ici à l'identique.
 */
export type CachedInternalMeeting = PlannedInternalMeeting & {
  /** Le jour du point à l'heure de Paris, posé par {@link replaceInternalMeetings}. */
  scheduledDay: string | null
}

/**
 * La saisie en cours, **un brouillon par contrôle** (`inquiryId` en clé primaire) : l'agent
 * n'ouvre pas deux fiches pour la même demande, et cette contrainte évite d'avoir à choisir
 * laquelle transmettre.
 *
 * `idempotencyKey` **naît avec le brouillon** (D4) : c'est elle qui rend inoffensif le rejeu
 * d'une transmission interrompue, y compris après un redémarrage de la tablette. La régénérer à
 * l'envoi produirait exactement le doublon qu'elle existe pour empêcher — **un seul geste la
 * retire**, la reprise d'un envoi dont le serveur a dit que la clé portait déjà une autre
 * demande (`retrySubmission`), où la garder condamnerait le relevé au même refus éternel.
 *
 * `lockedAt` est posé à la transmission : **transmettre, c'est verrouiller**, l'éditeur passe
 * en lecture seule et la fiche devient immuable côté terrain. Seule une reprise le remet à
 * `null` : celle d'un envoi refusé ou en échec — rien n'est enregistré, la fiche n'existe alors
 * que sur cette tablette — ou celle d'un contrôle que le bureau a renvoyé.
 */
export interface InspectionDraft {
  inquiryId: string
  idempotencyKey: string
  /** Le relevé, à la version que {@link InspectionDraft.surveyVersion} annonce. */
  survey: SurveyV2
  /**
   * La version du **schéma sous lequel ce relevé a été saisi**, portée par le brouillon lui-même.
   *
   * ⚠️ **Absente sur un brouillon commencé avant la montée en v2**, et c'est le cas qui compte :
   * elle se lit alors comme une v1, ce qui est la vérité de ce qu'il y a sur le disque. La
   * transmission monte ce relevé-là en v2 (`upgradeSurvey`) avant de l'annoncer — l'invariant du
   * dépôt est qu'un relevé est **toujours** validé contre le schéma de la version annoncée, et il
   * vaut aussi en écriture. Estampiller `2` sans regarder ferait partir un `methodsUsed` portant
   * `COMCOM` sous une version qui ne connaît plus cette valeur, sans que la montée — qui la
   * retire (#117) — soit passée.
   *
   * ⚠️ **Aucune migration Dexie n'est nécessaire, et aucune n'est posée** : ce champ n'est pas
   * indexé (`drafts: 'inquiryId'`), donc l'ajouter ne change pas le schéma des magasins. Son
   * absence *est* l'information.
   *
   * Ce qui est **déjà en file d'envoi** ne bouge jamais : l'enveloppe est figée à la mise en
   * file, version du relevé comprise.
   */
  surveyVersion?: number
  anomalies: SubmittedAnomaly[]
  /**
   * La conclusion que l'agent a **posée à la main** (#86, M19), ou `null` quand il laisse la
   * règle conclure — `undefined` sur un brouillon ouvert avant ce champ, qui se lit pareil.
   *
   * ⚠️ **Rien n'est écrit tant que l'agent n'a pas touché au contrôle segmenté**, et l'absence
   * est l'information : une fiche s'ouvre avant le contrôle, donc sur zéro anomalie, et y poser
   * la dérivation du moment figerait « Raccordé » sur une fiche que personne n'a encore
   * remplie. `resolveInspectionResult` (`@ghp/domain`) relit la règle à chaque rendu tant que la
   * place est vide.
   */
  result?: InspectionResult | null
  metPerson: {
    lastName?: string | null
    firstName?: string | null
    /**
     * Son numéro, saisi sur l'onglet « Infos ». **Optionnel comme ses voisins** : une fiche
     * déjà en brouillon sur une tablette n'a pas la clé, et la relire ne doit pas échouer —
     * même régime, et même absence de migration Dexie, que `result` ci-dessus.
     */
    phone?: string | null
    capacity?: string | null
  } | null
  /**
   * « Une autre personne est présente » (#555) : cochée, `metPerson` est la personne rencontrée ;
   * décochée, c'est le contact de la demande (`metPersonOf`). Décocher ne vide pas `metPerson` — la
   * saisie revient si l'agent recoche. **Optionnelle** comme `phone` : absente sur une fiche
   * ouverte avant elle, elle se déduit (`isOtherPersonPresent`). Aucune migration Dexie, champ non
   * indexé.
   */
  otherPersonPresent?: boolean
  /**
   * La qualité constatée du **contact qui signe** (#555) — quand personne d'autre n'est sur
   * place. À part de `metPerson`, que `isOtherPersonPresent` lirait comme une saisie ; absente, la
   * qualité annoncée du contact part (`metPersonOf`). Aucune migration Dexie, champ non indexé.
   */
  signerCapacity?: string
  certifiedWaterPoints: boolean
  certifiedNoSepticTank: boolean
  /**
   * La case de consentement de Transmettre (#354, M37), facultative pour signer. **Optionnelle**
   * comme `phone` : un brouillon ouvert avant elle n'a pas la clé, et la relire ne doit pas
   * échouer. Aucune migration Dexie, champ non indexé.
   */
  consentedDataRetention?: boolean
  signatureMediaId: string | null
  signedAt: string | null
  /**
   * Quand la signature a été **verrouillée** (#352) : l'agent a touché « Valider la signature »,
   * ou il a transmis. Dès lors elle ne s'efface plus, et rien d'autre de la fiche ne se fige avec
   * elle — le schéma, les pièces et le relevé restent ouverts jusqu'à la transmission.
   *
   * ⚠️ Distinct de `signedAt`, qui date le **paraphe** et bouge à chaque trait : un paraphe se
   * trace en plusieurs traits, et verrouiller au relâcher du stylet couperait un nom en deux.
   *
   * **Local, jamais transmis** : le bureau reçoit la signature et sa date, pas ce geste.
   * `undefined` sur un brouillon ouvert avant ce champ ; aucune migration Dexie, champ non indexé.
   */
  signatureLockedAt?: string | null
  /**
   * **Le jour où le contrôle a eu lieu** (#115), en jour de calendrier (`2026-08-05`) : ce n'est
   * ni le jour du rendez-vous, ni celui de la clôture de la fiche — une tournée se saisit sur
   * place et se transmet parfois le soir, ou le lendemain.
   *
   * **Déclaré par l'agent, jamais déduit** : ni du rendez-vous, ni de l'ouverture de la fiche.
   * Un brouillon naît sans date, et tant qu'elle manque les trois écrans le disent au lieu de
   * l'inventer — seul l'agent sait quand il est passé. Rien ne le réaligne sur le rendez-vous :
   * l'écart se montre, il ne se corrige pas (M21).
   *
   * ⚠️ Facultatif pour la même raison que `conflictCode` : un brouillon commencé avant ce champ
   * ne se réécrit pas au déploiement suivant, et le relire `undefined` ne doit pas faire tomber
   * l'éditeur.
   */
  performedOn?: string | null
  lat: number | null
  lon: number | null
  notes: string | null
  /**
   * « Travaux à réaliser » tels que l'agent les a écrits (#345). **Absent ou `null` : il n'y a pas
   * touché**, et la fiche montre puis transmet ce que `suggestRecommendations` propose ; `''` :
   * il les a retirés. Même régime que `result`, sans migration Dexie, champ non indexé.
   */
  recommendations?: string | null
  lockedAt: string | null
  /**
   * La correction du bureau (#437) dont ce brouillon est reparti — son `correctedAt`, tel que la
   * tournée l'a servi. C'est ce qui fait qu'une correction ne se reprend **qu'une fois** : à la
   * descente suivante, la même ne réécrit pas le schéma. `undefined` sur un brouillon qui n'en a
   * jamais repris ; aucune migration Dexie, le champ n'est pas indexé.
   */
  officeSchematicAt?: string | null
  /** Horodatage **local** — celui de la tablette, pas du serveur. */
  updatedAt: string
  /**
   * Quand la tournée servie a cessé de porter cette demande — une réaffectation, presque
   * toujours (#147).
   *
   * C'est le **seul** signal dont la tablette dispose : le cache des demandes ne se vide
   * jamais (`offline/pull.ts`), donc « la demande n'est plus là » ne s'y lit pas. Sans cette
   * marque, une saisie dont la demande est partie chez l'équipe voisine n'a plus aucun chemin
   * d'écran — « Mes contrôles » part des demandes.
   *
   * Posée et retirée par la seule descente **complète**, dans les deux sens : la demande
   * revient dans la tournée, la marque s'en va.
   *
   * `undefined` sur les brouillons écrits avant ce champ, et sur ceux qu'aucune descente
   * complète n'a encore revus.
   */
  outOfTourAt?: string | null
}

/**
 * Les familles de médias qu'une tablette range.
 *
 * Deux d'entre elles ne sont **pas** des pièces jointes — elles sont produites par l'app, pas
 * prises par l'agent : `signature` (le paraphe) et `schematic` (l'image rendue du schéma
 * réseau, M11). C'est `isAttachment` qui porte cette ligne de partage, une seule fois.
 *
 * ⚠️ `schematic_photo` n'est pas `schematic` : c'est la photo d'un schéma fait sur papier, que
 * l'agent prend et classe comme une autre pièce jointe (#397).
 */
export type MediumType =
  'view' | 'manhole' | 'stormwater' | 'signature' | 'schematic' | 'schematic_photo'

/**
 * Une pièce jointe, **octets compris**. IndexedDB porte les `Blob` nativement : les encoder en
 * base64 coûterait un tiers de volume en plus, pour rien.
 *
 * `id` est un uuid v7 tiré par la tablette (M7) et sert **aussi** de clé à l'objet distant :
 * rejouer le `PUT` réécrit le même objet, sans registre à tenir.
 *
 * `uploadedAt` est l'état d'envoi **par média** — c'est lui qui permet à une reprise de sauter
 * ce qui est déjà monté au lieu de tout renvoyer sur une 4G de chantier.
 */
export interface LocalMedium {
  id: string
  inquiryId: string
  blob: Blob
  /**
   * ⚠️ **Union fermée, et elle vit en trois exemplaires** : ici, dans `submittedMediumSchema`
   * (`@ghp/domain`) et dans l'affichage du bureau. Un type ajouté ici sans l'être là-bas fait
   * refuser l'enveloppe entière au dépôt, sans qu'aucun écran de la tablette ne le voie venir.
   *
   * ⚠️ **Aucune migration Dexie** : `type` n'est pas indexé (`media: 'id, inquiryId'`), donc
   * élargir cette union ne change pas le schéma des magasins — même cas que la carte
   * Rendez-vous (#194) et le motif de renvoi (#169).
   *
   * `signature` et `schematic` sont des médias **produits** par la tablette, pas des pièces que
   * l'agent a prises : ni l'un ni l'autre n'entre dans les pièces jointes (`isAttachment`).
   */
  type: MediumType
  takenAt: string | null
  label: string | null
  uploadedAt: string | null
  /**
   * L'**empreinte du dessin** qu'une image `schematic` montre (`schematicFingerprint`), pour
   * que l'écran sache dire qu'elle date d'avant la dernière retouche. `null` sur tout autre
   * média — une photo ne montre pas le schéma.
   *
   * ⚠️ **Purement local, et il le reste** : l'enveloppe de transmission choisit ses champs un
   * par un (`buildSubmission`), donc cette empreinte ne part pas au bureau. Elle n'aurait rien
   * à y faire — c'est un outil de la tablette pour avertir l'agent avant qu'il transmette.
   *
   * ⚠️ Aucune migration Dexie : le champ n'est pas indexé (`media: 'id, inquiryId'`). Son
   * absence *est* une information : une image dont on ne sait pas ce qu'elle montre est traitée
   * comme périmée.
   */
  sourceFingerprint?: string | null
  /**
   * La pièce de la visite précédente que celle-ci recopie (#675) — l'identifiant du média
   * d'origine. Posée à la descente sur une contre-visite (`storeCarriedMedia`) : les octets sont
   * déjà dupliqués au stockage sous l'`id` de cette ligne, d'où un `uploadedAt` posé d'emblée.
   * `null` ou absente sur une pièce prise par l'agent.
   *
   * ⚠️ Aucune migration Dexie : le champ n'est pas indexé (`media: 'id, inquiryId'`).
   */
  carriedFrom?: string | null
}

/**
 * L'état d'un envoi, tel que l'agent le voit :
 * - `pending` — en attente du réseau, rejoué tout seul, de plus en plus espacé
 *   (`nextAttemptAt`) ; un 401 y reste aussi — il fait redescendre la session, et le refresh
 *   de la passe suivante le résout ;
 * - `sending` — une tentative est en cours ;
 * - `sent` — le serveur l'a rangé (première réception **ou** rejeu) ;
 * - `failed` — le serveur a refusé (4xx) ; visible avec son motif, plus retenté tout seul —
 *   le même envoi rendrait le même refus. « Réessayer l'envoi » (`rearmSubmission`) le remet
 *   en jeu, et `retrySubmission` rouvre la fiche quand c'est le contenu qui est en cause — un
 *   relevé trop lourd (#246) ;
 * - `rejected` — le serveur a dit **non, et rien n'est enregistré** (contrat du 409 de dépôt) :
 *   plus aucun rejeu **automatique**, l'entrée reste visible avec son motif jusqu'à ce qu'un
 *   humain tranche (M21). Jamais silencieux, jamais jeté — et jamais terminal non plus : le
 *   relevé n'existe que sur cette tablette, `retrySubmission` rouvre la fiche pour le sauver.
 *
 * Ajouter une valeur ici n'est pas une migration Dexie : le schéma n'indexe que des noms de
 * champs, pas leurs valeurs.
 */
export type OutboxState = 'pending' | 'sending' | 'sent' | 'failed' | 'rejected'

/**
 * Une transmission en file. L'enveloppe est **figée à la mise en file**, complète : un rejeu ne
 * recompose rien depuis le brouillon, il renvoie exactement ce qui avait été décidé.
 *
 * `lastError` est pour l'agent, `conflictCode` pour le code : le motif machine du refus
 * (`depositConflictSchema`) est ce qui dit à la reprise s'il faut retirer la clé d'idempotence.
 * `null` quand le serveur a refusé sans rien nommer de lisible — on ne devine pas un motif dont
 * dépend le sort d'un relevé ; **absent** sur une entrée que rien n'a refusée, ou écrite avant
 * que ce champ n'existe. Facultatif pour cette seconde raison : une entrée déjà en file sur une
 * tablette ne se réécrit pas au déploiement suivant.
 */
export interface OutboxEntry {
  id: string
  inquiryId: string
  submission: InspectionSubmission
  state: OutboxState
  attempts: number
  lastError: string | null
  conflictCode?: DepositConflictCode | null
  /**
   * Reprise programmée d'un incident transitoire : avant cet instant, les passes ordinaires
   * sautent l'entrée ; une passe forcée (reconnexion, geste de l'agent) passe outre.
   * Facultatif pour la même raison que `conflictCode` : les entrées déjà en file l'ignorent.
   */
  nextAttemptAt?: string | null
  /**
   * Refus de jeton **consécutifs**, distincts d'`attempts` : un `401` ne dit rien de l'envoi,
   * seulement de la session, et sa cadence de reprise ne doit pas hériter des incidents réseau
   * de la matinée. Remis à zéro par tout incident d'une autre nature et par le geste
   * « Réessayer l'envoi » — c'est ce qui le rend consécutif. Facultatif comme les deux
   * précédents, et **non indexé** : une entrée déjà en file l'ignore, sans migration.
   */
  tokenRefusals?: number
  createdAt: string
  sentAt: string | null
  /**
   * L'instant où l'agent a lu la ligne dans « Notifications » (#120) — absent tant qu'il ne l'a
   * pas fait. **Non indexé**, donc sans migration ; jamais envoyé, `buildSubmission` choisit ses
   * champs un par un. Même champ sur les avis de passage et les rendez-vous pris sur place.
   */
  readAt?: string | null
}

/**
 * Un **avis de passage** en attente d'envoi — « nous sommes venus, personne n'a ouvert ».
 *
 * ⚠️ **Sa propre file, et non une seconde forme d'`OutboxEntry`.** Les deux envois n'ont ni la
 * même charge utile, ni les mêmes reprises, ni le même sens : un dépôt de contrôle porte des
 * blobs, se reprend à la main et peut être renvoyé par le bureau ; un avis est un fait de trois
 * champs qui part une fois. Les mêler obligerait chaque lecteur de la file à discriminer, pour
 * qu'un seul des deux cas l'intéresse jamais.
 *
 * ⚠️ **`noticedAt` vient de la tablette, pas du serveur** (M24, et le contrat le dit) : l'agent
 * est souvent hors réseau devant la porte close — c'est précisément là que le signal manque —,
 * et son envoi part plus tard. Dater à l'arrivée citerait dans l'e-mail une heure à laquelle
 * personne n'est passé.
 *
 * ⚠️ **Dans la base de l'équipe**, comme la file des dépôts : un avis est le constat d'une
 * équipe, et le binôme suivant ne doit pas le voir dans sa file.
 */
export interface PassageNoticeEntry {
  id: string
  inquiryId: string
  /** L'instant du passage, relevé sur la tablette. */
  noticedAt: string
  reason: PassageNoticeReason
  state: OutboxState
  attempts: number
  lastError: string | null
  createdAt: string
  sentAt: string | null
  /** Voir {@link OutboxEntry.readAt}. */
  readAt?: string | null
}

/**
 * Un **rendez-vous pris sur place** en attente d'envoi — l'agent l'a fixé au porte-à-porte d'un
 * contrôle systématique (#268) ou d'une SYAM (#695).
 *
 * ⚠️ **Sa propre file**, pour la raison qui sépare déjà les avis de passage des dépôts : une autre
 * charge utile, une autre route (`POST /planning/field-appointments`), un envoi unique.
 *
 * ⚠️ **L'`id` est celui du rendez-vous**, tiré ici (uuid v7) et renvoyé tel quel à chaque
 * tentative : c'est lui que le serveur relit pour ne pas poser un second créneau au rejeu.
 *
 * ⚠️ **Tant qu'il n'est pas parti, il fait foi sur la tournée** (`cacheInquiries`) : le serveur ne
 * le connaît pas encore, et sa vue redescendue le ferait disparaître de l'agenda.
 */
export interface FieldAppointmentEntry {
  id: string
  inquiryId: string
  /** L'instant du rendez-vous, fuseau compris (M5). */
  scheduledAt: string
  /** « Nom de la personne », `null` quand il n'a pas été communiqué. */
  contactName: string | null
  contactPhone: string | null
  /** Absent des entrées posées avant le 08/10/2026. */
  comment?: string | null
  state: OutboxState
  attempts: number
  lastError: string | null
  createdAt: string
  sentAt: string | null
  /** Voir {@link OutboxEntry.readAt}. */
  readAt?: string | null
}

/**
 * L'**étape d'un contrôle** en attente d'envoi — commencé, signé (M42) : ce que le bureau voit
 * d'un contrôle encore sur la tablette.
 *
 * ⚠️ **Sa propre file**, pour la raison qui sépare déjà les avis de passage des dépôts : une autre
 * charge utile, une autre route (`POST /inspection-steps`), un envoi unique. L'`id` est celui de
 * l'étape côté serveur, tiré une fois : le rejeu retombe sur la même ligne.
 *
 * ⚠️ **Rien ne la montre à l'agent**, refusée ou non : elle sert le bureau, et son échec ne coûte
 * rien au contrôle. La ligne reste, avec son motif, pour le diagnostic.
 */
export interface InspectionStepEntry extends RecordInspectionStep {
  state: OutboxState
  attempts: number
  lastError: string | null
  createdAt: string
  sentAt: string | null
}

export type DataDatabase = Dexie & {
  inquiries: EntityTable<CachedInquiry, 'id'>
  drafts: EntityTable<InspectionDraft, 'inquiryId'>
  media: EntityTable<LocalMedium, 'id'>
  outbox: EntityTable<OutboxEntry, 'id'>
  passageNotices: EntityTable<PassageNoticeEntry, 'id'>
  appointments: EntityTable<FieldAppointmentEntry, 'id'>
  internalMeetings: EntityTable<CachedInternalMeeting, 'id'>
  inspectionSteps: EntityTable<InspectionStepEntry, 'id'>
}

/** Les tables d'une tournée — celles qu'une équipe garde pour elle seule. */
export const DATA_TABLES = [
  'inquiries',
  'drafts',
  'media',
  'outbox',
  'passageNotices',
  'appointments',
  'internalMeetings',
  'inspectionSteps',
] as const

export type DataTable = (typeof DATA_TABLES)[number]

/**
 * Les quatre files d'envoi — dépôts, avis de passage, rendez-vous pris sur place, étapes. Toutes
 * portent `inquiryId` et un `state` indexé : une ligne qui n'y est pas `sent` n'a pas encore été
 * reçue par le serveur.
 */
export const SEND_QUEUES = ['outbox', 'passageNotices', 'appointments', 'inspectionSteps'] as const

/**
 * Le préfixe des bases de tournée, **une par équipe** : `ghp-terrain-data-<clé d'équipe>`.
 *
 * ⚠️ Bases **distinctes de `ghp-terrain-session`**, qui porte la session : leurs cycles de vie
 * diffèrent. Une déconnexion volontaire efface la session, jamais les saisies (D22 pt.1).
 *
 * ⚠️ Et **une par équipe**, ce qui est le cloisonnement lui-même (D22 pt.4) : la tablette passe
 * de main en main, et l'équipe entrante ne lit pas la tournée de la sortante parce que sa base
 * **n'est pas ouverte** — pas parce qu'un filtre l'en empêche. Il n'y a donc rien à estampiller
 * à l'écriture, rien à filtrer à la lecture, et aucune de ces deux disciplines à tenir.
 */
export const DATA_DATABASE_PREFIX = 'ghp-terrain-data-'

/**
 * Le nom de la base d'une équipe, depuis sa **clé de périmètre** (`offline/team-scope.ts`) :
 * l'identifiant de l'équipe, ou `no-team` pour un agent qui n'en a pas (M6) — une session
 * valide, dont les saisies ont droit à leur propre base.
 */
export function dataDatabaseName(scopeKey: string): string {
  return `${DATA_DATABASE_PREFIX}${scopeKey}`
}

/**
 * Fabrique, pas singleton — même raison que `openSessionDatabase` : une instance partagée
 * ferait fuiter l'état d'un test au suivant, et rendrait l'ordre des tests significatif. Qui
 * ouvre, quand, et qui referme : `offline/team-data.ts`.
 *
 * ⚠️ **`version(1)` n'est pas un schéma neuf posé sur des données anciennes** : ces bases portent un nom que rien n'a jamais ouvert avant #158 — une base par
 * équipe, là où il n'y en avait qu'une, commune et filtrée. Aucune tablette n'en a donc de
 * version antérieure à migrer, et l'ancienne base n'est pas reprise : elle ne portait que des
 * données de test, et s'efface au démarrage (`offline/retired-data.ts`).
 *
 * ⚠️ Ce que la règle du dépôt continue d'interdire vaut à partir d'ici : **toute table ou index
 * qui s'ajoute passe par un `version(n)` de plus**, jamais par une retouche d'un bloc existant —
 * une tablette déjà en tournée porte des saisies non transmises.
 *
 * - `version(2)` — la file des **rendez-vous pris sur place** (#268).
 * - `version(3)` — les **points internes** de l'équipe (#319). Une table neuve, sans `upgrade()` :
 *   rien d'existant n'a à recevoir de champ, et la première descente la remplit.
 * - `version(4)` — la file des **étapes d'un contrôle** (M42, #556). Une table neuve, sans
 *   `upgrade()`.
 */
export function openDataDatabase(scopeKey: string): DataDatabase {
  const db = new Dexie(dataDatabaseName(scopeKey)) as DataDatabase

  db.version(1).stores({
    inquiries: 'id, scheduledDay',
    drafts: 'inquiryId',
    media: 'id, inquiryId',
    outbox: 'id, inquiryId, state',
    passageNotices: 'id, inquiryId, state',
  })
  // Une table de plus, rien à réécrire : les tables existantes gardent leurs lignes (#268).
  db.version(2).stores({ appointments: 'id, inquiryId, state' })
  db.version(3).stores({ internalMeetings: 'id, scheduledDay' })
  db.version(4).stores({ inspectionSteps: 'id, state' })

  return db
}

/**
 * Les demandes dont le rendez-vous tombe entre ces deux **jours de calendrier**, bornes
 * comprises (`2026-08-03` → `2026-08-08`) — ce que l'agenda affiche d'une semaine ou d'un mois.
 *
 * ⚠️ Lues par l'index `scheduledDay`, jamais par un filtre sur toute la table : le cache des
 * demandes garde **tout contrôle saisi**, transmis compris (`offline/pull.ts`), il grandit donc
 * sur toute la vie de la tablette, alors qu'une semaine en compte une dizaine. Balayer à chaque flèche « semaine
 * suivante » ferait payer l'historique entier à chaque geste de navigation.
 *
 * ⚠️ **Une demande sans rendez-vous n'en sort jamais**, par construction : IndexedDB n'indexe pas
 * une ligne dont la clé indexée manque. Elle reste dans la tournée, lue par `toArray()` —
 * l'agenda ne la perd pas, il ne la place pas sur une heure qu'elle n'a pas.
 */
export function scheduledBetween(
  db: DataDatabase,
  fromDay: string,
  toDay: string,
): Promise<CachedInquiry[]> {
  return db.inquiries.where('scheduledDay').between(fromDay, toDay, true, true).toArray()
}

/**
 * Les points internes qui **occupent** un jour entre ces deux **jours de calendrier**, bornes
 * comprises — la lecture de {@link scheduledBetween}, sur l'autre nature de créneau. Un séminaire
 * commencé avant `fromDay` en est (#576) : l'index se lit depuis le plus long qui soit.
 */
export function internalMeetingsBetween(
  db: DataDatabase,
  fromDay: string,
  toDay: string,
): Promise<CachedInternalMeeting[]> {
  return db.internalMeetings
    .where('scheduledDay')
    .between(shiftDays(fromDay, -INTERNAL_MEETING_MAX_DAYS), toDay, true, true)
    .filter((meeting) =>
      internalMeetingDays(meeting.scheduledAt, meeting.durationMinutes).some(
        (day) => day >= fromDay,
      ),
    )
    .toArray()
}

/** Le plus long point interne, en jours : jusqu'où un séminaire peut déborder sa fenêtre. */
const INTERNAL_MEETING_MAX_DAYS = INTERNAL_MEETING_MAX_DURATION_MINUTES / MINUTES_PER_DAY

/**
 * Le cache porte-t-il **au moins un** rendez-vous qui est encore du travail de l'équipe (`shows`),
 * toutes périodes confondues ?
 *
 * C'est le fait qui distingue « cette semaine ne porte rien » de « la tournée n'est pas
 * descendue » : sans lui, un agenda ouvert sur une semaine creuse — le cas courant — annonce une
 * panne de téléchargement, tournée en cache. ⚠️ Le rendez-vous d'un contrôle transmis ne compte
 * pas (#408) : le cache le garde à vie, et il ferait passer pour descendue une tournée dont la
 * tablette ne tient aucun travail.
 *
 * ⚠️ Lu par l'index, **du jour le plus récent au plus ancien**, et arrêté au premier rendez-vous
 * retenu : les contrôles transmis s'accumulent dans le passé, le travail est devant. Des bornes
 * `minKey`/`maxKey` plutôt que deux jours en dur : elles encadrent **toute** valeur indexable,
 * donc l'intervalle ne dépend d'aucune hypothèse sur la forme d'un jour.
 */
export async function anyScheduled(
  db: DataDatabase,
  shows: (inquiry: CachedInquiry) => boolean,
): Promise<boolean> {
  const first = await db.inquiries
    .where('scheduledDay')
    .between(Dexie.minKey, Dexie.maxKey, true, true)
    .reverse()
    .filter(shows)
    .first()

  return first !== undefined
}

/**
 * Une modification **visée par sa clé** : elle aboutit, ou elle lève.
 *
 * ⚠️ `Table.update()` rend `0` quand la ligne n'existe pas, et un `0` n'est ni un avertissement
 * ni un refus (M21) — c'est un mensonge : l'appelant vise toujours une ligne qu'il vient de
 * lire, il ne teste donc pas ce compte, et peint un succès sur une saisie que personne ne
 * retrouvera. Une donnée de terrain ne se reconstitue pas : elle se signale, et c'est l'écran
 * qui le dit (`writeFailureMessage`, éditeur de contrôle).
 *
 * ⚠️ Cette garde **ne dépend d'aucun cloisonnement** et survit à sa disparition (#158) : la
 * ligne absente est absente pour tout le monde, y compris pour l'agent qui vient de la saisir.
 */
export async function updateRow<T>(
  table: KeyedTable<T>,
  key: string,
  patch: UpdateSpec<T>,
): Promise<void> {
  if ((await table.update(key, patch)) === 0) throw new MissingRowError(table.name, key)
}

/**
 * La modification d'un brouillon : comme {@link updateRow}, elle aboutit ou elle lève — et elle
 * lève aussi {@link LockedDraftError} pour une saisie sur une fiche **transmise**.
 *
 * ⚠️ Le verrou se lit **dans le filtre du `modify`**, donc dans la transaction qui écrit. Un autre
 * onglet de la tablette partage cette base : relu à part, il pourrait transmettre entre la lecture
 * et l'écriture, et la saisie irait sur une fiche déjà partie.
 */
export async function updateDraft(
  db: DataDatabase,
  inquiryId: string,
  patch: UpdateSpec<InspectionDraft>,
): Promise<void> {
  const entry = touchesEntry(patch)

  const modified = await db.drafts
    .where('inquiryId')
    .equals(inquiryId)
    .and((row) => !(entry && isLocked(row)))
    .modify(patch)
  if (modified > 0) return

  // La relecture ne décide rien : elle nomme seulement la cause d'un refus déjà acquis.
  const existing = await db.drafts.get(inquiryId)
  if (existing === undefined) throw new MissingRowError(db.drafts.name, inquiryId)
  if (entry && isLocked(existing)) throw new LockedDraftError(inquiryId)
}

/**
 * Ce qu'une fiche **transmise** accepte encore : les champs que le verrou pose ou retire
 * (`enqueueSubmission`, `unlockDraft` dans `outbox.ts`) et la marque de la descente
 * (`outOfTourAt`, `pull.ts`), qui dit où en est la tournée, pas ce que l'agent a relevé. Tout le
 * reste est de la saisie.
 */
const LOCK_FIELDS: ReadonlySet<string> = new Set([
  'lockedAt',
  'idempotencyKey',
  'updatedAt',
  'outOfTourAt',
] satisfies (keyof InspectionDraft)[])

function touchesEntry(patch: object): boolean {
  return Object.keys(patch).some((field) => !LOCK_FIELDS.has(field))
}

function isLocked(draft: InspectionDraft): boolean {
  return draft.lockedAt != null
}

/**
 * Ce qu'{@link updateRow} demande d'une table : viser une ligne par sa clé, et savoir se nommer
 * dans un refus. Une forme structurelle plutôt que le type de Dexie, qui fait dépendre le type de
 * la clé de la table — les quatre tables d'une tournée ont toutes une clé primaire `string`, et
 * c'est la seule chose dont cette garde ait besoin.
 */
interface KeyedTable<T> {
  readonly name: string
  update(key: string, changes: UpdateSpec<T>): PromiseLike<number>
}

/**
 * L'échec d'une écriture qui n'a rien écrit : **aucune** ligne à cette clé.
 *
 * Reconnaissable, et pas seulement lisible : l'écran doit pouvoir en nommer la cause au lieu de
 * la confondre avec une base illisible. Une tablette qui va bien et une tablette en panne
 * n'appellent pas les mêmes gestes de l'agent, et un « rouvrez l'application » sur une saisie
 * bien vivante l'enverrait chercher la panne au mauvais endroit.
 */
export class MissingRowError extends Error {
  constructor(
    readonly table: string,
    readonly key: string,
  ) {
    super(`Aucune ligne à modifier (${table} ${key}) sur cette tablette : rien n'a été écrit.`)
    this.name = 'MissingRowError'
  }
}

/**
 * Une saisie visait une fiche **déjà transmise** : rien n'a été écrit.
 *
 * Le cas réel est la PWA ouverte deux fois sur la tablette (l'app installée et un onglet), qui
 * partagent une seule base. L'un transmet, l'autre garde la fiche ouverte et continue d'écrire :
 * sans ce refus la saisie entrait dans Dexie, ne partait jamais (l'enveloppe est figée à la mise
 * en file), et la fiche « Transmis » l'affichait ensuite comme si elle était partie.
 *
 * Pas {@link MissingRowError} : la ligne est bien là, et la conduite n'est pas la même — la fiche
 * est à relire telle qu'elle est partie.
 */
export class LockedDraftError extends Error {
  override readonly name = 'LockedDraftError'

  constructor(readonly inquiryId: string) {
    super(
      `Ce contrôle (${inquiryId}) a déjà été transmis depuis cette tablette : la saisie n'a pas été enregistrée.`,
    )
  }
}

/**
 * La descente range la tournée : l'un des **deux** chemins qui écrivent `inquiries` — l'autre est
 * le rendez-vous pris sur place ({@link showFieldAppointment}) —, et c'est ici que se dérivent ses
 * deux champs.
 *
 * ⚠️ `scheduledDay` et `servedAt` se dérivent **ici**, jamais chez l'appelant : un champ indexé
 * posé par l'appelant est un champ qu'un appelant peut oublier, et une ligne sans keyPath
 * n'entre dans aucun index — donc un rendez-vous absent de l'agenda, sans erreur.
 *
 * ⚠️ `servedAt` se passe quand l'appelant sait **quand la réponse a été demandée**, et c'est
 * alors cette date qui fait foi. L'heure d'écriture est plus tardive que celle de l'appel, et
 * l'écart est celui du réseau : une descente partie avant un dépôt accepté et atterrie après lui
 * rangerait la demande périmée sous une date postérieure à son `sentAt` — elle se lirait comme
 * un renvoi au terrain qui n'a jamais eu lieu.
 *
 * ⚠️ **Écrase sans demander la permission**, et c'est délibéré : une demande n'est la saisie de
 * personne, c'est la vue du serveur, redescendue à chaque passe. Ce qui est protégé, c'est le
 * relevé (`drafts`, `media`, `outbox`), pas son en-tête.
 */
export async function cacheInquiries(
  db: DataDatabase,
  inquiries: readonly AssignedInquiry[],
  servedAt: string = new Date().toISOString(),
): Promise<void> {
  // ⚠️ Un rendez-vous pris sur place et pas encore parti l'emporte sur la vue du serveur, qui ne le
  // connaît pas : sans lui, la descente le retirait de l'agenda en attendant le réseau.
  const waiting = new Map(
    (await db.appointments.where('state').anyOf(['pending', 'sending']).toArray()).map((entry) => [
      entry.inquiryId,
      entry,
    ]),
  )

  await db.inquiries.bulkPut(
    inquiries.map((served) => {
      const entry = waiting.get(served.id)
      const inquiry = entry === undefined ? served : withFieldAppointment(served, entry)

      return { ...inquiry, servedAt, scheduledDay: calendarDayOf(inquiry.scheduledAt) }
    }),
  )
}

/** Ce qu'un rendez-vous pris sur la tablette change à la ligne de tournée. */
type FieldAppointmentShown = Pick<
  FieldAppointmentEntry,
  'inquiryId' | 'scheduledAt' | 'contactName' | 'contactPhone'
> & {
  /** La durée réglée en déplaçant le rendez-vous (#578) ; absente, la ligne garde la sienne. */
  durationMinutes?: number
}

/**
 * Montre sur la tournée le rendez-vous que l'agent vient de prendre, **sans attendre le serveur**.
 *
 * ⚠️ **`servedAt` ne bouge pas** : il dit quand le serveur a servi la ligne, et c'est lui qui
 * départage « transmis » et « renvoyé au terrain ». Le réécrire ici ferait lire un renvoi du bureau
 * que personne n'a fait.
 */
export async function showFieldAppointment(
  db: DataDatabase,
  entry: FieldAppointmentShown,
): Promise<void> {
  const cached = await db.inquiries.get(entry.inquiryId)
  if (cached === undefined) throw new MissingRowError(db.inquiries.name, entry.inquiryId)

  const inquiry = withFieldAppointment(cached, entry)
  await db.inquiries.put({ ...inquiry, scheduledDay: calendarDayOf(inquiry.scheduledAt) })
}

/**
 * La demande telle que le serveur la servira une fois le rendez-vous reçu — la même règle que
 * `PlanningRepository.scheduleFromField` : un **nom** désigne une personne neuve, un **téléphone
 * seul** corrige le contact affiché, et sans contact il devient un contact **sans nom** (M24).
 */
function withFieldAppointment<T extends AssignedInquiry>(
  inquiry: T,
  entry: FieldAppointmentShown,
): T {
  const shown = inquiry.contact ?? null
  const capacity = shown?.capacity ?? null

  const contact =
    entry.contactName !== null
      ? {
          lastName: entry.contactName,
          firstName: null,
          phone: entry.contactPhone,
          email: null,
          capacity,
        }
      : entry.contactPhone === null
        ? shown
        : {
            ...(shown ?? { lastName: null, firstName: null, email: null, capacity }),
            phone: entry.contactPhone,
          }

  return {
    ...inquiry,
    scheduledAt: entry.scheduledAt,
    contact,
    ...(entry.durationMinutes === undefined
      ? {}
      : { appointmentDurationMinutes: entry.durationMinutes }),
  }
}

/**
 * Range les points internes que le serveur vient de servir, **à la place** de ceux qu'il ne sert
 * plus — mais seulement sur les jours que sa réponse couvre.
 *
 * ⚠️ **Ici on remplace, contrairement à la tournée** ({@link cacheInquiries}), et pour la raison
 * du référentiel (`pullVocabulary`) : un point interne n'est la saisie de personne. Un point
 * annulé ou déplacé au bureau qui resterait affiché enverrait l'équipe à une réunion qui n'a
 * plus lieu.
 *
 * ⚠️ …**dans la fenêtre servie, et nulle part ailleurs** (`from` inclus, `to` exclu). Le serveur
 * ne sert que les semaines à venir : ce qui est plus ancien n'est pas « annulé », il est hors de
 * la question posée, et l'agenda d'une semaine passée le montre encore.
 *
 * ⚠️ …et **les jours qu'il occupe** font foi, pas celui de son début : un séminaire posé avant
 * `from` qui la chevauche est servi tant qu'il existe, donc retiré quand il ne l'est plus (#576).
 *
 * ⚠️ **Une transaction pour le retrait et l'ajout** : entre les deux, l'agenda ne doit jamais
 * voir une semaine amputée.
 */
export async function replaceInternalMeetings(
  db: DataDatabase,
  { from, to, items }: TourInternalMeetings,
): Promise<void> {
  await db.transaction('rw', db.internalMeetings, async () => {
    const served = new Set(items.map((meeting) => meeting.id))
    // Un séminaire commencé avant `from` peut encore occuper la fenêtre (#576) : il se lit depuis
    // le plus long qui soit, et ne se retire que s'il la touche.
    const withdrawn = await db.internalMeetings
      .where('scheduledDay')
      .between(shiftDays(from, -INTERNAL_MEETING_MAX_DAYS), to, true, false)
      .filter(
        (meeting) =>
          !served.has(meeting.id) &&
          internalMeetingDays(meeting.scheduledAt, meeting.durationMinutes).some(
            (day) => day >= from && day < to,
          ),
      )
      .primaryKeys()

    await db.internalMeetings.bulkDelete(withdrawn)
    await db.internalMeetings.bulkPut(
      items.map((meeting) => ({ ...meeting, scheduledDay: calendarDayOf(meeting.scheduledAt) })),
    )
  })
}

/**
 * Le jour de calendrier d'un rendez-vous, **à l'heure de Paris** comme la fiche
 * (`appointmentSlot`), quel que soit le réglage de la tablette.
 *
 * ⚠️ Jamais `toISOString().slice(0, 10)`, qui rend le jour **UTC** : un rendez-vous de 0 h 30 en
 * heure d'été française tomberait la veille.
 *
 * `null` sur une date illisible plutôt qu'une levée : une descente ne doit pas échouer sur un
 * champ d'affichage (M21), et la demande reste dans la tournée — seul son créneau manque.
 */
export function calendarDayOf(scheduledAt: string | null | undefined): string | null {
  if (scheduledAt === null || scheduledAt === undefined) return null

  return appointmentFields(scheduledAt)?.day ?? null
}
